import { useMemo } from "react";
import { Box, Typography, Grid, Chip } from "@mui/material";
import { getStatusColor } from "./MprStatusRenderer";

export const renderApproverComment = (status, comment) => {
  if (comment && String(comment).trim() !== "") {
    return String(comment).trim();
  }
  if (status === "Rejected") {
    return "No rejection comment provided";
  }
  if (status === "Approved") {
    return "No comment provided";
  }
  if (status === "Pending" || status === "Submitted" || status?.includes("Awaiting")) {
    return "Awaiting review";
  }
  if (status === "Not Required") {
    return "Approval not required";
  }
  if (status === "Cancelled") {
    return "Request cancelled";
  }
  return "-";
};

const ApproverCommentsStatus = ({
  editedRecord,
  mprMeta: passedMprMeta,
  title = "Approver Comments & Status",
  isGridItem = true,
  sx = {},
}) => {
  const currentRecord = editedRecord || {};

  const mprMeta = useMemo(() => {
    if (passedMprMeta) return passedMprMeta;
    const raw = currentRecord?.mpr_metadata_json;
    if (typeof raw === "string") {
      try {
        return JSON.parse(raw);
      } catch (e) {
        return {};
      }
    }
    return raw || {};
  }, [passedMprMeta, currentRecord?.mpr_metadata_json]);

  const firstApproverName = currentRecord?.approver_name || "-";

  const hasSecondApprover = Boolean(currentRecord?.second_approver_employee_id);

  const secondApproverName = currentRecord?.second_approver_emp_name || "-";

  const hasThirdApprover = Boolean(currentRecord?.third_approver_employee_id);

  const thirdApproverName = currentRecord?.third_approver_emp_name || "-";

  const firstStatus = mprMeta?.first_status || "Pending";
  const firstComment = mprMeta?.first_comment;
  const firstApprovedCount = mprMeta?.first_approved_count;
  const firstApprovedBudget = mprMeta?.first_approved_budget;

  const secondStatus = mprMeta?.second_status || "Pending";
  const secondComment = mprMeta?.second_comment;
  const secondApprovedCount = mprMeta?.second_approved_count;
  const secondApprovedBudget = mprMeta?.second_approved_budget;

  const thirdStatus = mprMeta?.third_status || "Pending";
  const thirdComment = mprMeta?.third_comment;
  const thirdApprovedCount = mprMeta?.third_approved_count;
  const thirdApprovedBudget = mprMeta?.third_approved_budget;

  const firstCardSm = hasThirdApprover ? 4 : hasSecondApprover ? 6 : 12;
  const secondCardSm = hasThirdApprover ? 4 : 6;
  const thirdCardSm = 4;

  const cardContent = (
    <Box
      sx={{
        p: 2,
        borderRadius: "8px",
        border: "1px solid #e2e8f0",
        backgroundColor: "#f8fafc",
        ...sx,
      }}
    >
      <Typography
        sx={{
          fontSize: "14px",
          fontWeight: 600,
          color: "#1e293b",
          mb: 1.5,
        }}
      >
        {title}
      </Typography>

      <Grid container spacing={2}>
        {/* 1st Approver Card */}
        <Grid item xs={12} sm={firstCardSm}>
          <Box
            sx={{
              p: 1.5,
              borderRadius: "6px",
              border: "1px solid",
              borderColor:
                firstStatus === "Rejected"
                  ? "#fecaca"
                  : firstStatus === "Approved"
                  ? "#bbf7d0"
                  : "#e2e8f0",
              backgroundColor:
                firstStatus === "Rejected"
                  ? "#fff1f2"
                  : firstStatus === "Approved"
                  ? "#f0fdf4"
                  : "#ffffff",
              height: "100%",
              display: "flex",
              flexDirection: "column",
              gap: 0.75,
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                gap: 1,
              }}
            >
              <Typography
                sx={{ fontSize: "13px", fontWeight: 600, color: "#1e293b" }}
              >
                1st Approver
              </Typography>
              <Chip
                label={firstStatus}
                size="small"
                sx={{
                  height: 20,
                  fontSize: "10px",
                  fontWeight: 600,
                  backgroundColor: getStatusColor(firstStatus).bg,
                  color: getStatusColor(firstStatus).text,
                }}
              />
            </Box>
            <Typography
              variant="caption"
              sx={{ color: "#64748b", fontSize: "12px" }}
            >
              {firstApproverName}
            </Typography>
            <Box
              sx={{
                mt: 0.5,
                p: 1,
                borderRadius: "4px",
                backgroundColor: "rgba(0, 0, 0, 0.02)",
                border: "1px dashed #cbd5e1",
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  color: "#475569",
                  fontSize: "11px",
                  fontWeight: 600,
                  display: "block",
                }}
              >
                Comment / Remarks:
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  fontSize: "12px",
                  color: firstComment ? "#1e293b" : "#94a3b8",
                  fontStyle: firstComment ? "normal" : "italic",
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-word",
                }}
              >
                {renderApproverComment(firstStatus, firstComment)}
              </Typography>
            </Box>
            {firstApprovedCount && (
              <Typography
                variant="caption"
                sx={{ color: "#475569", fontSize: "11px" }}
              >
                Approved Count: <b>{firstApprovedCount}</b>
                {firstApprovedBudget ? ` | Budget: ${firstApprovedBudget}` : ""}
              </Typography>
            )}
          </Box>
        </Grid>

        {/* 2nd Approver Card */}
        {hasSecondApprover && (
          <Grid item xs={12} sm={secondCardSm}>
            <Box
              sx={{
                p: 1.5,
                borderRadius: "6px",
                border: "1px solid",
                borderColor:
                  secondStatus === "Rejected"
                    ? "#fecaca"
                    : secondStatus === "Approved"
                    ? "#bbf7d0"
                    : "#e2e8f0",
                backgroundColor:
                  secondStatus === "Rejected"
                    ? "#fff1f2"
                    : secondStatus === "Approved"
                    ? "#f0fdf4"
                    : "#ffffff",
                height: "100%",
                display: "flex",
                flexDirection: "column",
                gap: 0.75,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                <Typography
                  sx={{ fontSize: "13px", fontWeight: 600, color: "#1e293b" }}
                >
                  2nd Approver
                </Typography>
                <Chip
                  label={secondStatus}
                  size="small"
                  sx={{
                    height: 20,
                    fontSize: "10px",
                    fontWeight: 600,
                    backgroundColor: getStatusColor(secondStatus).bg,
                    color: getStatusColor(secondStatus).text,
                  }}
                />
              </Box>
              <Typography
                variant="caption"
                sx={{ color: "#64748b", fontSize: "12px" }}
              >
                {secondApproverName}
              </Typography>
              <Box
                sx={{
                  mt: 0.5,
                  p: 1,
                  borderRadius: "4px",
                  backgroundColor: "rgba(0, 0, 0, 0.02)",
                  border: "1px dashed #cbd5e1",
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    color: "#475569",
                    fontSize: "11px",
                    fontWeight: 600,
                    display: "block",
                  }}
                >
                  Comment / Remarks:
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    fontSize: "12px",
                    color: secondComment ? "#1e293b" : "#94a3b8",
                    fontStyle: secondComment ? "normal" : "italic",
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                  }}
                >
                  {renderApproverComment(secondStatus, secondComment)}
                </Typography>
              </Box>
              {secondApprovedCount && (
                <Typography
                  variant="caption"
                  sx={{ color: "#475569", fontSize: "11px" }}
                >
                  Approved Count: <b>{secondApprovedCount}</b>
                  {secondApprovedBudget ? ` | Budget: ${secondApprovedBudget}` : ""}
                </Typography>
              )}
            </Box>
          </Grid>
        )}

        {/* 3rd Approver Card */}
        {hasThirdApprover && (
          <Grid item xs={12} sm={thirdCardSm}>
            <Box
              sx={{
                p: 1.5,
                borderRadius: "6px",
                border: "1px solid",
                borderColor:
                  thirdStatus === "Rejected"
                    ? "#fecaca"
                    : thirdStatus === "Approved"
                    ? "#bbf7d0"
                    : "#e2e8f0",
                backgroundColor:
                  thirdStatus === "Rejected"
                    ? "#fff1f2"
                    : thirdStatus === "Approved"
                    ? "#f0fdf4"
                    : "#ffffff",
                height: "100%",
                display: "flex",
                flexDirection: "column",
                gap: 0.75,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                <Typography
                  sx={{ fontSize: "13px", fontWeight: 600, color: "#1e293b" }}
                >
                  3rd Approver
                </Typography>
                <Chip
                  label={thirdStatus}
                  size="small"
                  sx={{
                    height: 20,
                    fontSize: "10px",
                    fontWeight: 600,
                    backgroundColor: getStatusColor(thirdStatus).bg,
                    color: getStatusColor(thirdStatus).text,
                  }}
                />
              </Box>
              <Typography
                variant="caption"
                sx={{ color: "#64748b", fontSize: "12px" }}
              >
                {thirdApproverName}
              </Typography>
              <Box
                sx={{
                  mt: 0.5,
                  p: 1,
                  borderRadius: "4px",
                  backgroundColor: "rgba(0, 0, 0, 0.02)",
                  border: "1px dashed #cbd5e1",
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    color: "#475569",
                    fontSize: "11px",
                    fontWeight: 600,
                    display: "block",
                  }}
                >
                  Comment / Remarks:
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    fontSize: "12px",
                    color: thirdComment ? "#1e293b" : "#94a3b8",
                    fontStyle: thirdComment ? "normal" : "italic",
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                  }}
                >
                  {renderApproverComment(thirdStatus, thirdComment)}
                </Typography>
              </Box>
              {thirdApprovedCount && (
                <Typography
                  variant="caption"
                  sx={{ color: "#475569", fontSize: "11px" }}
                >
                  Approved Count: <b>{thirdApprovedCount}</b>
                  {thirdApprovedBudget ? ` | Budget: ${thirdApprovedBudget}` : ""}
                </Typography>
              )}
            </Box>
          </Grid>
        )}
      </Grid>
    </Box>
  );

  if (isGridItem) {
    return <Grid item xs={12}>{cardContent}</Grid>;
  }

  return cardContent;
};

export default ApproverCommentsStatus;
